import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MessageCircle, 
  Award,
  Calendar,
  Heart,
  Users,
  Target,
  Layers,
  Dumbbell,
  Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { contactInfo, siteConfig } from '../lib/data';
import PageMeta from '../components/PageMeta';

const About = () => {
  const qualifications = [
    { year: '2003', title: 'Degree in Social Science' },
    { year: '2005', title: 'Injury & Sports Treatments Qualification' },
    { year: '2018', title: 'Nutritional Therapy (3-year qualification)' },
    { year: 'Cert.', title: 'Advanced Dry Needling (Acupuncture)' },
    { year: 'Member', title: 'Nutritional Therapists of Ireland (NTOI)' },
  ];

  const approachPoints = [
    'Guide clients toward sustainable, long-term health improvements',
    'Support and encourage through every stage of recovery',
    'Challenge clients to make meaningful changes',
    'Educate on the connection between nutrition, movement, and wellbeing',
  ];

  const differentiators = [
    {
      icon: Layers,
      title: 'Combined Therapy & Nutrition',
      description: 'A unique combination of hands-on neuromuscular therapy and evidence-based nutritional support, addressing the body as a whole rather than treating symptoms in isolation.',
    },
    {
      icon: Target,
      title: 'Root-Cause Approach',
      description: 'Rather than masking symptoms, Ali focuses on identifying and addressing the underlying causes of pain, dysfunction, and poor health through functional assessment and testing.',
    },
    {
      icon: Dumbbell,
      title: 'Broad Clinical Experience',
      description: 'From acute sports injuries and chronic pain to hormonal health, fertility, and performance nutrition — Ali brings over two decades of hands-on experience across a wide range of conditions.',
    },
    {
      icon: Heart,
      title: 'Personal Sporting Background',
      description: 'As a former competitive athlete, Ali understands first-hand the physical demands of sport, the importance of recovery, and the role nutrition plays in performance and resilience.',
    },
  ];

  return (
    <div data-testid="about-page">
      <PageMeta
        title="About Ali Dorgan | Neuromuscular & Nutritional Therapist"
        description="Learn about Ali Dorgan's qualifications, approach, and over two decades of experience in neuromuscular therapy and nutritional support in Cobh, Cork."
      />
      {/* 1. Hero */}
      <section className="py-20 md:py-28 bg-cream-100">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-xs uppercase tracking-[0.2em] text-sage-500 font-semibold mb-6 block">
                About
              </span>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-charcoal leading-tight mb-4">
                About Ali
              </h1>
              <p className="text-lg md:text-xl text-charcoal/60 mb-8">
                Neuromuscular & Nutritional Therapist based in Cobh, Cork
              </p>
              <p className="text-sm text-sage-600 font-medium border-l-2 border-sage-400 pl-4">
                {siteConfig.credentials}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="rounded-2xl lg:rounded-3xl overflow-hidden shadow-soft max-w-lg mx-auto">
                <img
                  src="/ali-hero.jpg"
                  alt="Ali Dorgan — Neuromuscular & Nutritional Therapist"
                  className="w-full h-auto object-cover"
                  loading="eager"
                  data-testid="about-hero-image"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 2. Introduction */}
      <section className="py-24 md:py-32 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-xs uppercase tracking-[0.2em] text-sage-500 font-semibold mb-6 block">
                Background
              </span>
              <h2 className="font-serif text-4xl md:text-5xl font-light text-charcoal mb-10">
                From Competitive Sport to Healthcare
              </h2>
              <div className="space-y-6 text-charcoal/70 text-lg leading-relaxed">
                <p>
                  Ali's passion and interest in wellbeing comes from her background in 
                  competitive sports. Understanding the physical demands of training and 
                  competition first-hand sparked a deep interest in how the body works, 
                  recovers, and performs at its best.
                </p>
                <p>
                  That passion led to a career built on helping others — first through 
                  hands-on physical therapy, and later through a comprehensive understanding 
                  of how nutrition supports everything from injury recovery to long-term 
                  health. Today, Ali combines both disciplines to offer a truly holistic 
                  approach to wellbeing.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3. Qualifications & Experience */}
      <section className="py-24 md:py-32 bg-sage-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-xs uppercase tracking-[0.2em] text-sage-500 font-semibold mb-6 block">
              Qualifications
            </span>
            <h2 className="font-serif text-4xl md:text-5xl font-light text-charcoal">
              Education & Training
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto">
            {qualifications.map((qual, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="flex gap-6 mb-6 last:mb-0"
              >
                <div className="flex-shrink-0 w-20">
                  <span className="font-serif text-lg text-sage-500">{qual.year}</span>
                </div>
                <div className="flex-1 pb-6 border-b border-sage-200 last:border-0">
                  <div className="flex items-center gap-3">
                    <Award className="w-5 h-5 text-sage-500 shrink-0" />
                    <h3 className="font-medium text-charcoal text-lg">{qual.title}</h3>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Approach */}
      <section className="py-24 md:py-32 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-xs uppercase tracking-[0.2em] text-sage-500 font-semibold mb-6 block">
                Approach
              </span>
              <h2 className="font-serif text-4xl md:text-5xl font-light text-charcoal mb-8">
                How We Work
              </h2>
              <p className="text-charcoal/70 text-lg leading-relaxed mb-10">
                We work closely with each client to make long-term, meaningful changes 
                that improve their health and quality of life. Every treatment and nutrition 
                plan is individualised — there are no one-size-fits-all approaches.
              </p>
              <p className="text-charcoal/70 text-lg leading-relaxed">
                By combining hands-on neuromuscular therapy with personalised nutritional 
                support, Ali addresses the body as a whole. The focus is always on 
                sustainable results, not quick fixes.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div className="rounded-2xl lg:rounded-3xl overflow-hidden shadow-soft">
                <img
                  src="/ali-therapy.jpg"
                  alt="Ali Dorgan performing hands-on neuromuscular therapy in her clinic"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                  data-testid="about-therapy-image"
                />
              </div>
              <div className="space-y-4">
                {approachPoints.map((point, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-4 bg-cream-50 rounded-xl p-5"
                  >
                    <div className="w-6 h-6 bg-sage-100 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-sage-600" />
                    </div>
                    <span className="text-charcoal/80 text-base">{point}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 5. What Makes Her Different */}
      <section className="py-24 md:py-32 bg-cream-100">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-xs uppercase tracking-[0.2em] text-sage-500 font-semibold mb-6 block">
              The Difference
            </span>
            <h2 className="font-serif text-4xl md:text-5xl font-light text-charcoal">
              What Sets Ali Apart
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {differentiators.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full bg-white border-0 shadow-soft rounded-2xl">
                  <CardContent className="p-8 md:p-10">
                    <div className="w-14 h-14 bg-sage-100 rounded-2xl flex items-center justify-center mb-6">
                      <item.icon className="w-7 h-7 text-sage-600" />
                    </div>
                    <h3 className="font-serif text-xl font-medium text-charcoal mb-3">
                      {item.title}
                    </h3>
                    <p className="text-charcoal/60 text-base leading-relaxed">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. CTA */}
      <section className="py-24 md:py-32 bg-sage-900">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-4xl md:text-5xl font-light text-cream-100 mb-8">
              Ready to Get Started?
            </h2>
            <p className="text-cream-200/80 text-lg mb-10 max-w-xl mx-auto leading-relaxed">
              Whether you're dealing with an injury, seeking nutritional guidance, or 
              looking to improve your long-term health, Ali is here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-5 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-[#25D366] hover:bg-[#20BD5C] text-white rounded-full px-10 py-6 gap-2 font-semibold hover:-translate-y-0.5 transition-all duration-300"
                data-testid="about-cta-whatsapp"
              >
                <a href={contactInfo.whatsapp} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="w-5 h-5" />
                  Message Ali
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                className="bg-white text-charcoal hover:bg-cream-100 rounded-full px-10 py-6 font-semibold hover:-translate-y-0.5 transition-all duration-300"
                data-testid="about-cta-book"
              >
                <Link to="/book">
                  <Calendar className="w-5 h-5 mr-2" />
                  Book Appointment
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
